'use client';

import type { ReactNode } from 'react';
import type { HouseholdOnboardingDetailSheetAccount } from 'src/features/advisor-book-onboarding/components/household-onboarding-detail-sheet-gql';
import type { HouseholdOnboardingStatusRibbonRow } from 'src/features/advisor-book-onboarding/components/household-onboarding-status-ribbon-gql';
import type { OnboardingCockpitStatus } from 'src/features/advisor-book-onboarding/constants/cockpit-url-state';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Badge } from '@savvy-kit/components/badge';
import { Button } from '@savvy-kit/components/button';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@savvy-kit/components/sheet';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import Banner from '@savvy-kit/patterns/banner';
import { isNil } from 'es-toolkit';
import { ArrowUpRight, Check, FileText, LoaderCircle } from 'lucide-react';
import { OnboardingCockpitStatusBadge } from 'src/features/advisor-book-onboarding/components/cockpit/onboarding-cockpit-status-badge';
import { HouseholdOnboardingDetailSheetAccountsQuery } from 'src/features/advisor-book-onboarding/components/household-onboarding-detail-sheet-gql';
import {
  accountNumberLast4,
  accountStatusBadgeFor,
  billableAssetsCaption,
  billableTransferPct,
  blockedBannerDescription,
  blockedBannerTitle,
} from 'src/features/advisor-book-onboarding/components/household-onboarding-detail-sheet-utils';
import {
  householdOnboardingPrimaryCtaLabel,
  householdOnboardingPrimaryHrefFor,
} from 'src/features/advisor-book-onboarding/components/household-onboarding-primary-cta';
import { OnboardingSendReminderMenu } from 'src/features/advisor-book-onboarding/components/onboarding-send-reminder-menu';
import { EM_DASH } from 'src/features/advisor-book-onboarding/constants/cockpit-status-copy';
import {
  householdOnboardingDetailStateMeansFor,
  householdOnboardingPrimaryCtaFor,
} from 'src/features/advisor-book-onboarding/constants/household-onboarding-status-copy';
import { ADVISOR_BOOK_ONBOARDING_ROUTES } from 'src/features/advisor-book-onboarding/constants/routes';
import { useResendOnboardingInvite } from 'src/features/advisor-book-onboarding/hooks/use-resend-onboarding-invite';
import { useSendOnboardingInviteToOps } from 'src/features/advisor-book-onboarding/hooks/use-send-onboarding-invite-to-ops';
import { formatLargeDollarAmount, formatTimestampToMonthAndDay } from 'src/utils/formatting';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

type HouseholdOnboardingDetailSheetProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly row: HouseholdOnboardingStatusRibbonRow;
  readonly dismissing?: boolean;
  readonly onFixContactInfo?: () => void;
  readonly onDismiss?: () => void;
  readonly onMarkComplete?: () => void;
  readonly onInviteResent?: () => void;
};

export function HouseholdOnboardingDetailSheet({
  open,
  onOpenChange,
  row,
  dismissing = false,
  onFixContactInfo,
  onDismiss,
  onMarkComplete,
  onInviteResent,
}: HouseholdOnboardingDetailSheetProps) {
  const router = useRouter();
  const { confirmResendInvite } = useResendOnboardingInvite();
  const { confirmSendInviteToOps } = useSendOnboardingInviteToOps();

  const {
    data,
    loading: accountsLoading,
    error: accountsError,
  } = useQueryWithErrorLogging(HouseholdOnboardingDetailSheetAccountsQuery, {
    variables: { id: row.uuid },
    skip: !open,
    fetchPolicy: 'network-only',
  });

  const householdPayload = data?.advisorOnboardingStagedHousehold ?? null;
  const stagedAccounts = householdPayload?.onboardingAccounts ?? [];
  const accountsFailed = !isNil(accountsError) || (!accountsLoading && data !== undefined && isNil(householdPayload));
  const primaryCta = householdOnboardingPrimaryCtaFor({
    status: row.status,
    blockedReason: row.blockedReason,
  });
  const stateMeans = householdOnboardingDetailStateMeansFor(row.status);
  const householdUuid =
    typeof router.query['salesforceAccountId'] === 'string' ? router.query['salesforceAccountId'] : null;
  const householdHref = householdUuid !== null ? ADVISOR_BOOK_ONBOARDING_ROUTES.householdOverview(householdUuid) : null;
  const primaryHref = householdOnboardingPrimaryHrefFor({
    kind: primaryCta?.kind,
    householdPublicId: row.householdUuid ?? null,
  });

  function handleResendInvite() {
    confirmResendInvite({
      stagedHouseholdId: row.id,
      householdName: row.householdName,
      recipients: row.inviteRecipients,
      primaryClientName: row.primaryClientName,
      primaryClientEmail: row.primaryClientEmail,
      onSuccess: onInviteResent,
    });
  }

  function handleSendToOps() {
    if (isNil(row.householdUuid)) {
      return;
    }
    confirmSendInviteToOps({
      householdPublicId: row.householdUuid,
      householdName: row.householdName,
      onSuccess: onInviteResent,
    });
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent size="lg" className="sm:max-w-lg">
        <SheetHeader className="gap-3">
          <div className="flex min-w-0 flex-col gap-3">
            <div>
              <div className="flex items-baseline gap-2">
                <SheetTitle className="font-display text-2xl font-normal leading-8">{row.householdName}</SheetTitle>
                {householdHref !== null && (
                  <Button
                    variant="link"
                    size="sm"
                    nativeButton={false}
                    render={<Link href={householdHref} />}
                    aria-label={`View ${row.householdName}`}
                    className="h-auto min-h-11 gap-0.5 px-1 py-2 text-xs font-medium text-secondary"
                  >
                    View
                    <ArrowUpRight className="size-3.5" aria-hidden="true" />
                  </Button>
                )}
              </div>
              {!isNil(row.primaryClientName) && row.primaryClientName !== '' && (
                <SheetDescription className="mt-0.5">{row.primaryClientName}</SheetDescription>
              )}
            </div>
            <OnboardingCockpitStatusBadge
              status={row.status}
              blockedReason={row.blockedReason}
              accountsTransferred={row.accountsTransferred}
              accountsTotal={row.accountsTotal}
              primaryClientName={row.primaryClientName}
              lastContactedAt={row.lastContactedAt}
            />
            <DetailActions
              status={row.status}
              primaryCta={primaryCta}
              primaryHref={primaryHref}
              householdName={row.householdName}
              dismissing={dismissing}
              onResendInvite={handleResendInvite}
              onSendToOps={handleSendToOps}
              sendToOpsDisabled={isNil(row.householdUuid)}
              onFixContactInfo={onFixContactInfo}
              onDismiss={onDismiss}
              onMarkComplete={onMarkComplete}
            />
          </div>
        </SheetHeader>

        <SheetBody className="space-y-6">
          <BillableAssetsSummary estAum={row.estAum} transferredAmount={row.transferredAmount} status={row.status} />

          <PanelSection title="What this state means">
            <Text variant="body-sm" color="dark" className="leading-relaxed">
              {stateMeans}
            </Text>
          </PanelSection>

          {row.status === 'awaiting_client' && (
            <AwaitingClientSections
              msaSigned={row.msaSigned}
              msaTotal={row.msaTotal}
              lastContactedAt={row.lastContactedAt}
            />
          )}

          {row.status === 'in_progress' && (
            <PanelSection title="Transfer progress">
              <Text variant="body-sm" color="dark">
                {row.accountsTransferred} of {row.accountsTotal} accounts transferring.
              </Text>
            </PanelSection>
          )}

          {row.status === 'blocked' && (
            <Banner
              variant="destructive"
              title={blockedBannerTitle(row.blockedReason)}
              description={blockedBannerDescription(row.blockedReason)}
              hideIcon={false}
            />
          )}

          {row.status === 'complete' && (
            <PanelSection title="Shipped">
              <ul className="space-y-1.5">
                <ShippedItem label="All signatures collected" />
                <ShippedItem label={`Accounts transferred (${row.accountsTotal})`} />
              </ul>
            </PanelSection>
          )}

          <AccountsSection
            accounts={stagedAccounts}
            loading={accountsLoading}
            failed={accountsFailed}
            status={row.status}
          />
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

type DetailActionsProps = {
  readonly status: OnboardingCockpitStatus;
  readonly primaryCta: ReturnType<typeof householdOnboardingPrimaryCtaFor>;
  readonly primaryHref: string | null;
  readonly householdName: string;
  readonly dismissing: boolean;
  readonly onResendInvite: () => void;
  readonly onSendToOps: () => void;
  readonly sendToOpsDisabled: boolean;
  readonly onFixContactInfo?: () => void;
  readonly onDismiss?: () => void;
  readonly onMarkComplete?: () => void;
};

function DetailActions({
  status,
  primaryCta,
  primaryHref,
  householdName,
  dismissing,
  onResendInvite,
  onSendToOps,
  sendToOpsDisabled,
  onFixContactInfo,
  onDismiss,
  onMarkComplete,
}: DetailActionsProps) {
  if (status === 'awaiting_client') {
    return (
      <div className="mt-auto flex flex-wrap gap-2">
        <OnboardingSendReminderMenu
          householdName={householdName}
          onResendToClient={onResendInvite}
          onSendToOps={onSendToOps}
          sendToOpsDisabled={sendToOpsDisabled}
        />
      </div>
    );
  }

  if (status === 'in_progress' && !isNil(onMarkComplete)) {
    return (
      <div className="mt-auto flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={onMarkComplete} aria-label={`Mark ${householdName} as complete`}>
          Mark as complete
        </Button>
      </div>
    );
  }

  if (isNil(primaryCta)) {
    return null;
  }

  if (primaryHref !== null) {
    return (
      <div className="mt-auto flex flex-wrap gap-2">
        <Button size="sm" nativeButton={false} render={<Link href={primaryHref} />}>
          {householdOnboardingPrimaryCtaLabel(primaryCta.kind)}
        </Button>
      </div>
    );
  }

  // confirm_accounts / send_onboarding are link-only (they route into the ECI flow); with no household
  // public id there is nothing to open, and the action fallback below would otherwise fire onDismiss.
  if (primaryCta.kind !== 'fix_contact_info' && primaryCta.kind !== 'dismiss') {
    return null;
  }

  const onClick = primaryCta.kind === 'fix_contact_info' ? onFixContactInfo : onDismiss;

  return (
    <div className="mt-auto flex flex-wrap gap-2">
      <Button size="sm" onClick={onClick} disabled={primaryCta.kind === 'dismiss' && dismissing}>
        {householdOnboardingPrimaryCtaLabel(primaryCta.kind)}
      </Button>
    </div>
  );
}

type BillableAssetsSummaryProps = {
  readonly estAum: number | null;
  readonly transferredAmount: number | null;
  readonly status: OnboardingCockpitStatus;
};

function BillableAssetsSummary({ estAum, transferredAmount, status }: BillableAssetsSummaryProps) {
  const est = estAum ?? 0;
  const transferred = transferredAmount ?? 0;
  const pct = billableTransferPct({ est, transferred, status });
  const size = 96;
  const stroke = 9;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct);
  const isFull = pct >= 0.999;
  const caption = billableAssetsCaption({ est, transferred, status });
  const pctLabel = `${Math.round(pct * 100)}% of estimated billable assets transferred`;

  return (
    <div className="flex items-center gap-3">
      <div className="relative size-24 shrink-0">
        <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
          <circle cx={cx} cy={cx} r={r} fill="none" className="stroke-[var(--border)]" strokeWidth={stroke} />
          {pct > 0 && (
            <circle
              cx={cx}
              cy={cx}
              r={r}
              fill="none"
              className="stroke-primary"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circ}
              strokeDashoffset={offset}
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <Text variant="subtitle-sm" className={cn('tabular-nums', isFull ? 'text-primary' : 'text-foreground')}>
            {!isNil(estAum) ? formatLargeDollarAmount(estAum) : EM_DASH}
          </Text>
        </div>
        <span className="sr-only">{pctLabel}</span>
      </div>
      <div>
        <Text variant="caption" color="dark" className="font-medium">
          Billable assets
        </Text>
        <Text variant="caption" color="light">
          {caption}
        </Text>
      </div>
    </div>
  );
}

type PanelSectionProps = {
  readonly title: string;
  readonly children: ReactNode;
};

function PanelSection({ title, children }: PanelSectionProps) {
  return (
    <div>
      <Text variant="subtitle-sm" color="dark" render={<h3 />}>
        {title}
      </Text>
      <div className="mt-2">{children}</div>
    </div>
  );
}

type AwaitingClientSectionsProps = {
  readonly msaSigned: number;
  readonly msaTotal: number;
  readonly lastContactedAt: string | null;
};

function AwaitingClientSections({ msaSigned, msaTotal, lastContactedAt }: AwaitingClientSectionsProps) {
  const lastContactedLabel =
    !isNil(lastContactedAt) && lastContactedAt !== '' ? formatTimestampToMonthAndDay(lastContactedAt) : null;
  const showLastContacted = lastContactedLabel !== null && lastContactedLabel !== '';

  return (
    <PanelSection title="Signatures">
      <Text variant="body-sm" color="dark">
        {msaSigned} of {msaTotal} signed
      </Text>
      {showLastContacted && (
        <Text variant="caption" color="light" className="mt-1">
          Last contacted {lastContactedLabel}
        </Text>
      )}
    </PanelSection>
  );
}

type ShippedItemProps = {
  readonly label: string;
};

function ShippedItem({ label }: ShippedItemProps) {
  return (
    <li className="flex items-center gap-2">
      <Check className="size-4 shrink-0 text-primary" aria-hidden="true" />
      <Text variant="body-sm" color="dark">
        {label}
      </Text>
    </li>
  );
}

type AccountsSectionProps = {
  readonly accounts: readonly HouseholdOnboardingDetailSheetAccount[];
  readonly loading: boolean;
  readonly failed: boolean;
  readonly status: OnboardingCockpitStatus;
};

function AccountsSection({ accounts, loading, failed, status }: AccountsSectionProps) {
  if (loading) {
    return (
      <PanelSection title="Accounts">
        <div className="space-y-1.5" aria-busy="true" role="status">
          <span className="sr-only">Loading accounts</span>
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </PanelSection>
    );
  }

  if (failed) {
    return (
      <PanelSection title="Accounts">
        <Text variant="body-sm" color="light" render={<p role="alert" />}>
          Couldn&apos;t load accounts. Close and try again.
        </Text>
      </PanelSection>
    );
  }

  return (
    <PanelSection title={`Accounts (${accounts.length})`}>
      {accounts.length === 0 ? (
        <Text variant="body-sm" color="light">
          No accounts staged for this household.
        </Text>
      ) : (
        <ul className="space-y-1.5">
          {accounts.map((account) => (
            <AccountRow key={account.id} account={account} status={status} />
          ))}
        </ul>
      )}
    </PanelSection>
  );
}

type AccountRowProps = {
  readonly account: HouseholdOnboardingDetailSheetAccount;
  readonly status: OnboardingCockpitStatus;
};

function AccountRow({ account, status }: AccountRowProps) {
  const last4 = accountNumberLast4(account.number);
  const typeLabel = account.accountType ?? account.accountName ?? 'Account';
  const title = last4 !== null ? `${typeLabel} ··${last4}` : typeLabel;
  const badge = accountStatusBadgeFor(status);

  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-light bg-card px-3 py-2">
      <div className="min-w-0">
        <Text variant="body-sm" color="dark" className="truncate font-medium">
          {title}
        </Text>
        {!isNil(account.custodian) && account.custodian !== '' && (
          <Text variant="caption" color="light" className="truncate">
            {account.custodian}
          </Text>
        )}
      </div>
      <Badge variant={badge.variant} className="w-auto shrink-0">
        {badge.kind === 'complete' && <Check aria-hidden="true" />}
        {badge.kind === 'in_progress' && <LoaderCircle aria-hidden="true" className="animate-spin" />}
        {badge.kind === 'awaiting' && <FileText aria-hidden="true" />}
        {badge.label}
      </Badge>
    </li>
  );
}
